import { API_VERSION } from '../../../../util/constants';
import { HttpQuery } from '../../../../util/http-query';
import { get } from '../../../../util/httpService';


type PensionOffsetParams = { duration: { from: string; to: string } };
type CacheKey = string;
type CacheEntry = { data: any; expiry: number };

const DEFAULT_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
const pensionOffsetCache: Map<CacheKey, CacheEntry> = new Map();

function deepStableStringify(obj: any, seen = new WeakSet()): string {
  if (obj === null) return 'null';
  if (typeof obj !== 'object') return JSON.stringify(obj);
  if (seen.has(obj)) throw new TypeError('Converting circular structure to JSON');
  seen.add(obj);

  if (Array.isArray(obj)) {
    return '[' + obj.map(item => deepStableStringify(item, seen)).join(',') + ']';
  }

  const keys = Object.keys(obj).sort();
  const props = keys.map(key => {
    const val = obj[key];
    // skip undefined, functions, symbols for cache key
    if (typeof val === 'undefined' || typeof val === 'function' || typeof val === 'symbol') return '';
    return JSON.stringify(key) + ':' + deepStableStringify(val, seen);
  }).filter(Boolean);
  return '{' + props.join(',') + '}';
}


function getCacheKey(employeeID: string, params: PensionOffsetParams): string {
  return `${employeeID}:${deepStableStringify(params)}`;
}

export const getEmployeePensionOffset = async (
  employeeID: string,
  params: PensionOffsetParams
): Promise<any> => {
  const key = getCacheKey(employeeID, params);
  const now = Date.now();
  const cached = pensionOffsetCache.get(key);

  if (cached && cached.expiry > now) {
    return cached.data;
  }

  const endpoint = HttpQuery.serialize(
    params,
    `${API_VERSION}/pension-offset/pension-offset-and-salary-change/${employeeID}`,
  );
  const res = await get(endpoint);
  const data = res || [];
  pensionOffsetCache.set(key, { data, expiry: now + DEFAULT_CACHE_DURATION_MS });
  return data;
};
